/**
 * Les pictogrammes de la barre d'onglets.
 *
 * Dessinés à la main plutôt qu'importés : six traits ne valent pas une dépendance, et une
 * bibliothèque d'icônes arriverait avec son propre style de trait, sa propre grille et sa
 * propre idée du poids — trois choses que la charte décide déjà.
 *
 * Tous sur une grille de 24, trait de 1,6 px, extrémités rondes. Ils héritent de la
 * couleur du texte (`currentColor`) : c'est l'onglet qui décide s'il est actif, pas
 * l'icône.
 *
 * Aucun n'est annoncé aux lecteurs d'écran — le libellé sous l'icône dit déjà le mot.
 */

import type { ReactNode } from 'react';

interface IconProps {
  size?: number | undefined;
}

function Svg({ size = 22, children }: IconProps & { children: ReactNode }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {children}
    </svg>
  );
}

/** Accueil — le toit sous lequel tout est rassemblé. */
export function IconHome(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M4 10.4 12 4l8 6.4V19a1.5 1.5 0 0 1-1.5 1.5h-13A1.5 1.5 0 0 1 4 19z" />
      <path d="M9.5 20.5v-6h5v6" />
    </Svg>
  );
}

/** Activité — le tracé d'un effort, pas une chaussure ni un haltère. */
export function IconActivity(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M3 12.5h3.5L9 6l4 12 2.5-5.5H21" />
    </Svg>
  );
}

/** Nutrition — un bol, et ce qui s'en échappe. */
export function IconNutrition(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M3.5 12h17a8.5 8.5 0 0 1-17 0Z" />
      <path d="M6 20.5h12" />
      <path d="M9.5 8.5c0-1.4 1.2-1.8 1.2-3.2" />
      <path d="M14 8.5c0-1.4 1.2-1.8 1.2-3.2" />
    </Svg>
  );
}

/** Noter — l'action centrale : ajouter une mesure. */
export function IconPlus(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M12 5.5v13M5.5 12h13" />
    </Svg>
  );
}

/** Le reste de la navigation. */
export function IconMore(props: IconProps) {
  return (
    <Svg {...props}>
      <circle cx="5.5" cy="12" r="1.4" fill="currentColor" stroke="none" />
      <circle cx="12" cy="12" r="1.4" fill="currentColor" stroke="none" />
      <circle cx="18.5" cy="12" r="1.4" fill="currentColor" stroke="none" />
    </Svg>
  );
}

/** Corps — la balance. */
export function IconScale(props: IconProps) {
  return (
    <Svg {...props}>
      <rect x="3.5" y="4.5" width="17" height="15" rx="3" />
      <path d="M9 9.5a3 3 0 0 1 6 0" />
      <path d="M12 9.5v3.5" />
    </Svg>
  );
}

/** Routine — le verre et la gélule, réunis sous une goutte. */
export function IconDroplet(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M12 3.5c3.2 3.6 5.5 6.3 5.5 9.2a5.5 5.5 0 0 1-11 0c0-2.9 2.3-5.6 5.5-9.2Z" />
    </Svg>
  );
}

/** Planning — la grille des jours. */
export function IconCalendar(props: IconProps) {
  return (
    <Svg {...props}>
      <rect x="3.5" y="5.5" width="17" height="15" rx="2.5" />
      <path d="M3.5 10h17M8.5 3.5v4M15.5 3.5v4" />
    </Svg>
  );
}

/** Objectif — la cible. */
export function IconTarget(props: IconProps) {
  return (
    <Svg {...props}>
      <circle cx="12" cy="12" r="8.5" />
      <circle cx="12" cy="12" r="4" />
      <circle cx="12" cy="12" r="1" fill="currentColor" stroke="none" />
    </Svg>
  );
}

/** Dicter — le micro. */
export function IconMic(props: IconProps) {
  return (
    <Svg {...props}>
      <rect x="9" y="3.5" width="6" height="11" rx="3" />
      <path d="M5.5 11.5a6.5 6.5 0 0 0 13 0M12 18v2.5" />
    </Svg>
  );
}

/** Plan de course — le drapeau d'arrivée. */
export function IconFlag(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M5.5 20.5v-16" />
      <path d="M5.5 4.5h11l-2.5 4 2.5 4h-11" />
    </Svg>
  );
}

/** Assiduité — la grille annuelle, en quatre cellules. */
export function IconGrid(props: IconProps) {
  return (
    <Svg {...props}>
      <rect x="3.5" y="3.5" width="7" height="7" rx="1.5" />
      <rect x="13.5" y="3.5" width="7" height="7" rx="1.5" />
      <rect x="3.5" y="13.5" width="7" height="7" rx="1.5" />
      <rect x="13.5" y="13.5" width="7" height="7" rx="1.5" />
    </Svg>
  );
}

/** Assistant — la question qu'on pose. */
export function IconChat(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M20.5 12.5c0 3.9-3.8 7-8.5 7a9.8 9.8 0 0 1-2.6-.35L4 20.5l1.5-3.7A6.6 6.6 0 0 1 3.5 12.5c0-3.9 3.8-7 8.5-7s8.5 3.1 8.5 7Z" />
    </Svg>
  );
}

/** Réglages — le curseur qu'on déplace. */
export function IconSliders(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M4 8h10M18 8h2M4 16h4M12 16h8" />
      <circle cx="16" cy="8" r="2.2" />
      <circle cx="10" cy="16" r="2.2" />
    </Svg>
  );
}

/** Déconnexion — la porte. */
export function IconExit(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M14.5 4.5h3a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2h-3" />
      <path d="M10 8.5 13.5 12 10 15.5M13.5 12h-9" />
    </Svg>
  );
}

/** Les discussions — une pile de fils. */
export function IconThreads(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M4 6.5h16M4 12h16M4 17.5h10" />
    </Svg>
  );
}

/** La mémoire — le carnet qu'on garde ouvert à côté. */
export function IconBook(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M4.5 5.5a1.5 1.5 0 0 1 1.5-1.5h4a2.5 2.5 0 0 1 2.5 2.5v13a2 2 0 0 0-2-2H6a1.5 1.5 0 0 1-1.5-1.5Z" />
      <path d="M19.5 5.5A1.5 1.5 0 0 0 18 4h-4a2.5 2.5 0 0 0-2.5 2.5v13a2 2 0 0 1 2-2H18a1.5 1.5 0 0 0 1.5-1.5Z" />
    </Svg>
  );
}

/** Fait — la coche, dans un cercle qu'on peut viser au pouce. */
export function IconCheck(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M20.5 11.3V12a8.5 8.5 0 1 1-5-7.8" />
      <path d="m8.4 11.6 3.2 3.2 9-9" />
    </Svg>
  );
}

/**
 * Poids du corps — une silhouette, pas une balance.
 *
 * `IconScale` sert déjà à la pesée, où « poids » désigne celui de la personne mesurée.
 * Ici il désigne l'absence de charge. Deux sens pour un glyphe se paient à l'appui : on
 * distingue donc le corps qui porte de la balance qui mesure.
 */
export function IconBodyweight(props: IconProps) {
  return (
    <Svg {...props}>
      <circle cx="12" cy="5" r="2.5" />
      <path d="M12 8v7" />
      <path d="M6.5 10.5 12 9l5.5 1.5" />
      <path d="m9 21 3-6 3 6" />
    </Svg>
  );
}

/** Une charge — l'haltère, pour remettre un chiffre là où il n'y en a plus. */
export function IconWeight(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M4 9v6" />
      <path d="M7 6.5v11" />
      <path d="M17 6.5v11" />
      <path d="M20 9v6" />
      <path d="M7 12h10" />
    </Svg>
  );
}

/**
 * Favori — l'étoile, creuse pour « ajouter », pleine pour « c'est fait » (`NUT-17`).
 *
 * `filled` et non deux icônes : c'est le même objet dans deux états, et deux tracés
 * distincts finiraient par ne plus se superposer au pixel près.
 */
export function IconStar({ filled = false, ...props }: IconProps & { filled?: boolean }) {
  return (
    <Svg {...props}>
      <path
        d="M12 3.6l2.6 5.2 5.8.9-4.2 4.1 1 5.7L12 16.8l-5.2 2.7 1-5.7-4.2-4.1 5.8-.9z"
        fill={filled ? 'currentColor' : 'none'}
      />
    </Svg>
  );
}

/**
 * Partage — la flèche qui sort de la boîte, celle d'iOS.
 *
 * C'est le geste que le bouton déclenche : sur un téléphone, `navigator.share` ouvre
 * exactement cette feuille. Une icône d'image aurait annoncé un fichier, là où ce qui
 * s'ouvre est un choix de destination.
 */
export function IconShare(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M12 3.5v11" />
      <path d="M8.2 7.3 12 3.5l3.8 3.8" />
      <path d="M6.5 11.5H5A1.5 1.5 0 0 0 3.5 13v6A1.5 1.5 0 0 0 5 20.5h14a1.5 1.5 0 0 0 1.5-1.5v-6a1.5 1.5 0 0 0-1.5-1.5h-1.5" />
    </Svg>
  );
}

/**
 * Scanner — un code-barres dans les quatre coins d'un viseur (`NUT-28`).
 *
 * Les coins disent « viser », les barres disent « quoi » : un code-barres seul se lirait
 * comme un produit, un viseur seul comme un appareil photo.
 */
export function IconScan(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M4 8V5.5A1.5 1.5 0 0 1 5.5 4H8M16 4h2.5A1.5 1.5 0 0 1 20 5.5V8M20 16v2.5a1.5 1.5 0 0 1-1.5 1.5H16M8 20H5.5A1.5 1.5 0 0 1 4 18.5V16" />
      <path d="M8 8.5v7M10.5 8.5v7M13 8.5v7M16 8.5v7" />
    </Svg>
  );
}
